function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    console.log(1111111111);

    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    // 找到搜索框
    const searchBox = document.querySelector('.headerSearch_box1');
    const searchBoxHeight = searchBox ? searchBox.offsetHeight : 0;

    // 计算 header 底部位置，减去搜索框高度
    const headerBottom =
      this.header.getBoundingClientRect().bottom - this.borderOffset - searchBoxHeight;

    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(headerBottom)}px`);

    this.header.classList.add('menu-open');

    // 👉 打开菜单时隐藏搜索框
    if (searchBox) searchBox.style.display = 'none';

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

    closeMenuDrawer(event, elementToFocus) {
      if (!elementToFocus) return;
      super.closeMenuDrawer(event, elementToFocus);
      this.header.classList.remove('menu-open');
      window.removeEventListener('resize', this.onResize);

      // 👉 标记刚关闭时间（防止 header hide 抖动）
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) stickyHeader.lastMenuClosed = Date.now();

      // 👉 关闭菜单时显示搜索框
      const searchBox = document.querySelector('.headerSearch_box1');
      if (searchBox) searchBox.style.display = '';
    }


  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          var swiper = new Swiper('.product-recommendations .alsolikeSwiper', {
            observer: true,
            observeParents: true,
            scrollbar: {
              el: '.recommendations_bottom .swiper-scrollbar', // 修正为正确的容器
            },
            navigation: {
              nextEl: '.recommendations_bottom .swiper-button-next',
              prevEl: '.recommendations_bottom .swiper-button-prev',
            },
            pagination: {
              el: '.recommendations_bottom .swiper-pagination',
              clickable: true,
            },
            breakpoints: {
              320: {
                //当屏幕宽度大于等于320
                slidesPerView: 1,
                spaceBetween: 10,
              },
              768: {
                //当屏幕宽度大于等于768
                slidesPerView: 2.5,
                spaceBetween: 16,
              },
              1280: {
                //当屏幕宽度大于等于1280
                slidesPerView: 4,
                spaceBetween: 24,
              },
            },
          });
          // =============================
          // 点击 Quick Add 打开弹窗
          // =============================
          document.querySelectorAll('.productItem_quickAdd').forEach(btn => {
            btn.addEventListener('click', function (e) {
              e.preventDefault();
              const handle = this.dataset.productHandle;
              const sectionId = this.dataset.sectionId;

              // ✅ 查找当前卡片里的 Judge.me 评分块
              const parentCard = this.closest('.productItem') || this.parentElement;
              const judgeWidget = parentCard?.querySelector('.jdgm-widget.jdgm-preview-badge');

              if (!handle) {
                alert('Product handle 为空，请检查 Liquid 渲染');
                return;
              }

              openProductPopup(handle, sectionId, judgeWidget);
            });
          });
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


class _CartDrawerRecommendations extends HTMLElement {
  static recommendationsCache = {};

  async connectedCallback() {
    if (!_CartDrawerRecommendations.recommendationsCache[this.productId]) {
      _CartDrawerRecommendations.recommendationsCache[this.productId] =
        fetch(`${window.shopUrl}${window.routes.productRecommendationsUrl}?product_id=${this.productId}&limit=10&section_id=${this.sectionId}`);
    }

    const response = await _CartDrawerRecommendations.recommendationsCache[this.productId];
    const div = document.createElement("div");
    div.innerHTML = await response.clone().text();
    const productRecommendationsElement = div.querySelector("cart-drawer-recommendations");

    if (productRecommendationsElement && productRecommendationsElement.hasChildNodes()) {
      this.innerHTML = productRecommendationsElement.innerHTML;

      // 👉 渲染完成后绑定事件
      initVariantEvents(this);
    } else {
      this.hidden = true;
    }

    // 初始化 swiper
    this.initSwiper();
    window.addEventListener("resize", this.initSwiper.bind(this));
  }

  initSwiper() {
    const container = this.querySelector(".cartSwiper");

    if (!this.cartSwiper && container) {
      this.cartSwiper = new Swiper(container, {
        observer: true,
        observeParents: true,
        slidesPerView: 1,
        spaceBetween: 10,
        navigation: {
          nextEl: ".mini-cart__recommendations .swiper-button-next",
          prevEl: ".mini-cart__recommendations .swiper-button-prev",
        },
      });
    }
  }

  get productId() {
    return this.getAttribute("product-id");
  }
  get sectionId() {
    return this.getAttribute("section-id");
  }
};
customElements.define("cart-drawer-recommendations", _CartDrawerRecommendations);



function initVariantEvents(container = document) {
  container.querySelectorAll('.product-card, .productItem').forEach(productCard => {
    productCard.addEventListener('click', (e) => {
      const item = e.target.closest('.variant-option');
      if (!item) return;

      // 主图
      const mainImage =
        productCard.querySelector('.productItem_default .media_box img.product-item__primary-image') ||
        productCard.querySelector('.productItem_default .media_box img') ||
        productCard.querySelector('img.product-item__primary-image');

      const getImageFromItem = (el) => {
        if (!el) return '';
        return el.dataset.image ||
          el.querySelector('.variant-item-list[data-image]')?.dataset.image ||
          el.querySelector('[data-image]')?.dataset.image ||
          '';
      };

      const setMainImage = (url) => {
        if (!mainImage || !url) return;
        const picture = mainImage.closest('picture');
        if (picture) {
          picture.querySelectorAll('source').forEach(s => s.setAttribute('srcset', url));
        }
        if (mainImage.hasAttribute('srcset')) mainImage.setAttribute('srcset', url);
        mainImage.src = url;
      };

      // 移除兄弟 active
      const parentGroup = item.parentElement;
      parentGroup.querySelectorAll('.variant-option.active').forEach(i => i.classList.remove('active'));
      item.classList.add('active');

      // 更新主图
      const img = getImageFromItem(item.querySelector('.variant-item-list') || item);
      if (img) setMainImage(img);

      // 更新隐藏 input
      const variantId = (item.querySelector('.variant-item-list') || item).dataset.variantId;
      if (variantId) {
        const hiddenInput = productCard.querySelector('input.product-variant-id');
        if (hiddenInput) {
          hiddenInput.value = variantId;
          if (item.querySelector('.variant-item-list')?.dataset.inventory === "0") {
            hiddenInput.disabled = true;
          } else {
            hiddenInput.disabled = false;
          }
        }
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initVariantEvents(); // 默认给全局商品卡片绑定
});

// ==========================
// Tab + Swiper + 悬停 + 移动端弹窗逻辑
// ==========================
document.addEventListener('DOMContentLoaded', function () {

  const isMobile = window.innerWidth <= 999;

  // ==========================
  // 移动端弹窗改造
  // ==========================
  document.querySelectorAll('.portfolioSwiper').forEach((swiperBox) => {
    const allPopups = [];
    const allConters = [];

    swiperBox.querySelectorAll('.productPortfolio-conter').forEach((conter) => {
      const popup = conter.querySelector('.productPortfolio_conterBox');
      if (!popup) return;

      allPopups.push(popup);
      allConters.push(conter);

      if (isMobile) {
        // 移动端：统一隐藏，移到底部
        popup.classList.add('mobile-popup');
        popup.style.display = 'none';
        swiperBox.appendChild(popup);

        const trigger = conter.querySelector('.serialNumber');
        if (trigger) {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();

            if (popup.style.display === 'block') {
              popup.style.display = 'none';
              conter.classList.remove('active');
            } else {
              allPopups.forEach((p) => (p.style.display = 'none'));
              allConters.forEach((c) => c.classList.remove('active'));
              popup.style.display = 'block';
              conter.classList.add('active');
            }
          });
        }
      } else {
        // PC端：保留在原位
        popup.classList.remove('mobile-popup');
        popup.style.display = '';
        conter.appendChild(popup);
      }
    });
  });

  // ==========================
  // 更新箭头显示
  // ==========================
  function updateArrows(activeIndex) {
    const arrows = document.querySelectorAll('.detailedTab_right .detailedTab_arrow');
    arrows.forEach(a => a.style.display = 'none');

    const activePortfolio = document.querySelectorAll('.productPortfolio')[activeIndex];
    if (!activePortfolio) return;

    const slides = activePortfolio.querySelectorAll('.portfolioSwiper .swiper-slide:not(.swiper-slide-duplicate)');
    if (slides.length > 1) {
      arrows[activeIndex].style.display = 'block';
    }
  }

  // ==========================
  // 初始化单个 Swiper
  // ==========================
  function initSwiperFor(portfolioEl) {
    const swiperContainer = portfolioEl.querySelector('.portfolioSwiper');
    if (!swiperContainer || typeof Swiper !== 'function') return;

    // ⚠️ 如果高度为 0，稍后重试
    if (swiperContainer.clientHeight === 0) {
      
      setTimeout(() => initSwiperFor(portfolioEl), 100);
      return;
    }

    // 避免重复初始化
    if (portfolioEl.swiperInstance) {
     
      return;
    }

    const index = Array.from(document.querySelectorAll('.productPortfolio')).indexOf(portfolioEl);
    const arrows = document.querySelectorAll('.detailedTab_right .detailedTab_arrow')[index];
    const nextEl = arrows.querySelector('.swiper-button-next');
    const prevEl = arrows.querySelector('.swiper-button-prev');

    let paginationEl = null;
    if (isMobile) {
      paginationEl = swiperContainer.querySelector('.swiper-pagination');
      if (!paginationEl) {
        paginationEl = document.createElement('div');
        paginationEl.className = 'swiper-pagination';
        swiperContainer.appendChild(paginationEl);
      }
    }

    const swiperInstance = new Swiper(swiperContainer, {
      navigation: isMobile ? false : { nextEl, prevEl },
      slidesPerView: isMobile ? 'auto' : 1,
      spaceBetween: isMobile ? 8 : 16,
      pagination: isMobile
        ? { el: paginationEl, clickable: true }
        : false,
      observer: true,
      observeParents: true,
      breakpoints: {
        320: { slidesPerView: 1, spaceBetween: 10 },
        768: { slidesPerView: 1.4, spaceBetween: 16 },
        1280: { slidesPerView: 1.7, spaceBetween: 16 },
      },
    });

    portfolioEl.swiperInstance = swiperInstance;

    // 延迟更新尺寸，保险处理
    setTimeout(() => swiperInstance.update(), 300);


  }

  // ==========================
  // 初始化所有可见 Swiper
  // ==========================
  function initSwipers() {
    
    document.querySelectorAll('.productPortfolio').forEach(p => {
      
      if (p.offsetParent !== null) {
       
        initSwiperFor(p);
      }
    });
  }

  // ==========================
  // 初始化 Tab 默认状态
  // ==========================
  function initTabs() {
    const tabs = document.querySelectorAll('.detailedTab_list');
    const containers = document.querySelectorAll('.productPortfolio_container');
    const portfolios = document.querySelectorAll('.productPortfolio');

    if (!tabs.length) return;

    // 1️⃣ 清空所有状态
    tabs.forEach(t => t.classList.remove('active'));
    containers.forEach(c => c.style.display = 'none');
    portfolios.forEach(p => p.style.display = 'none');

    // 2️⃣ 激活第一个 tab
    const firstTab = tabs[0];
    firstTab.classList.add('active');

    const firstDataId = firstTab.dataset.id;

    // 3️⃣ 找到对应 container
    const targetContainer = Array.from(containers).find(c =>
      c.querySelector(`.productPortfolio[data-id="${firstDataId}"]`)
    );

    if (!targetContainer) return;

    targetContainer.style.display = 'block';

    // 4️⃣ 只显示当前 data-id 的 portfolio
    const targetPortfolio = targetContainer.querySelector(`.productPortfolio[data-id="${firstDataId}"]`);

    if (targetPortfolio) {
      targetPortfolio.style.display = 'block';
      initSwiperFor(targetPortfolio);
    }

    updateArrows(0);
  }

  // ==========================
  // Tab 点击切换
  // ==========================
  document.querySelectorAll('.detailedTab_list').forEach((tab, index) => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.detailedTab_list').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      const dataId = tab.dataset.id;

      document.querySelectorAll('.productPortfolio_container').forEach(c => c.style.display = 'none');
      const targetContainer = Array.from(document.querySelectorAll('.productPortfolio_container')).find(c =>
        c.querySelector(`.productPortfolio[data-id="${dataId}"]`)
      );
      if (!targetContainer) return;
      targetContainer.style.display = 'block';

      targetContainer.querySelectorAll('.productPortfolio').forEach(p => p.style.display = 'none');
      const targetPortfolio = targetContainer.querySelector(`.productPortfolio[data-id="${dataId}"]`);
      if (!targetPortfolio) return;
      targetPortfolio.style.display = 'block';

      if (!targetPortfolio.swiperInstance) {
        initSwiperFor(targetPortfolio);
      } else {
        targetPortfolio.swiperInstance.update();
      }

      updateArrows(index);
    });
  });

 // ==========================
  // 悬停效果（PC）
  // 触发点：.productPortfolio-conter
  // ==========================
  document.querySelectorAll('.productPortfolio-conter').forEach(item => {
    const box = item.querySelector('.productPortfolio_conterBox');
    if (!box) return;

    // 鼠标进入「点」
    item.addEventListener('mouseenter', () => {
      // 关闭同级其它点
      item
        .closest('.productPortfolio_box')
        .querySelectorAll('.productPortfolio-conter')
        .forEach(sibling => {
          if (sibling !== item) {
            sibling.classList.remove('active');
            const siblingBox = sibling.querySelector('.productPortfolio_conterBox');
            if (siblingBox) siblingBox.style.display = 'none';
          }
        });

      item.classList.add('active');
      box.style.display = 'flex';
    });

    // 鼠标离开「点」
    item.addEventListener('mouseleave', e => {
      // 如果是进入弹窗，不关闭
      if (box.contains(e.relatedTarget)) return;

      item.classList.remove('active');
      box.style.display = 'none';
    });

    // 鼠标离开「弹窗」
    box.addEventListener('mouseleave', e => {
      // 如果是回到点上，不关闭
      if (item.contains(e.relatedTarget)) return;

      item.classList.remove('active');
      box.style.display = 'none';
    });
  });


  // ==========================
  // 执行初始化
  // ==========================
  initTabs();
  window.addEventListener('load', initSwipers); // 等页面资源加载完成再初始化
});


// =============================
// 全局弹窗容器
// =============================
const popupWrapper = document.createElement('div');
popupWrapper.id = 'global-product-popup';
popupWrapper.className = 'product-pop-up';
popupWrapper.style.display = 'none';
popupWrapper.innerHTML = `
  <div class="productPopUP_content">
    <div class="productPopUP_close">
       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 4L20 20" stroke="#333333" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4 20L20 4" stroke="#333333" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <div class="productPopUP_content_box">
     <div class="productPopUP_left"></div>
    <div class="productPopUP_right"></div>
    </div>
  </div>
`;
document.body.appendChild(popupWrapper);

// 背景点击关闭
popupWrapper.addEventListener('click', (e) => {
  if (e.target.id === 'global-product-popup') closePopup();
});

// 关闭按钮
popupWrapper.querySelector('.productPopUP_close').addEventListener('click', closePopup);

function closePopup() {
  popupWrapper.style.display = 'none';
  document.querySelector('.gradient')?.classList.remove('overflow_hidden');
  popupWrapper.querySelector('.productPopUP_left').innerHTML = '';
  popupWrapper.querySelector('.productPopUP_right').innerHTML = '';
}

// =============================
// 点击 Quick Add 打开弹窗
// =============================
document.querySelectorAll('.productItem_quickAdd').forEach(btn => {
  btn.addEventListener('click', function (e) {
    e.preventDefault();
    const handle = this.dataset.productHandle;
    const sectionId = this.dataset.sectionId;

    // ✅ 查找当前卡片里的 Judge.me 评分块
    const parentCard = this.closest('.productItem') || this.parentElement;
    const judgeWidget = parentCard?.querySelector('.jdgm-widget.jdgm-preview-badge');

    if (!handle) {
      alert('Product handle 为空，请检查 Liquid 渲染');
      return;
    }

    openProductPopup(handle, sectionId, judgeWidget);
  });
});

// =============================
// 打开弹窗函数
// =============================
function openProductPopup(handle, sectionId, judgeWidget) {
  popupWrapper.style.display = 'flex';
  document.querySelector('.gradient')?.classList.add('overflow_hidden');

  const leftBox = popupWrapper.querySelector('.productPopUP_left');
  const rightBox = popupWrapper.querySelector('.productPopUP_right');

  leftBox.innerHTML = rightBox.innerHTML = `<div class="popup-loading my_h_16 font-weight-500 t_color2">Loading...</div>`;

  fetch(`/products/${handle}.js`)
    .then(res => res.json())
    .then(product => {
      renderPopup(product, leftBox, rightBox, sectionId, judgeWidget);
      initVariants(product, popupWrapper);
    })
    .catch(err => {
      console.error('Fetch or render failed:', err);
      leftBox.innerHTML = rightBox.innerHTML = '<p class="popup-error my_h_16 font-weight-500 t_color2">Loading failed, please try again later.</p>';
    });
}


// =============================
// 渲染弹窗内容 + Quick Add
// =============================
function renderPopup(product, leftBox, rightBox, sectionId, judgeWidget) {
  const allMedia = product.media || [];
  const mediaWrapperHtml = [];

  // ====================
  // 左侧媒体
  // ====================
  (product.variants || []).forEach(variant => {
    const variantColor = variant.option1 || '';
    const mediaForVariant = allMedia.filter(m => {
      if (!m.alt) return false;
      const mediaAlt = m.alt.toLowerCase().replace(/[-_]/g, '');
      const variantNormalized = variantColor.toLowerCase().replace(/\s+/g, '');
      return mediaAlt.includes(variantNormalized);
    });

    const mediaHtml = mediaForVariant.map((m, i) => {
      if (m.media_type === 'image') {
        return `<div class="media-item swiper-slide" data-index="${i}">
                  <img 
                    loading="lazy"
                    decoding="async"
                    src="${m.src}"
                    srcset="${m.src_360 || m.src} 360w, ${m.src_720 || m.src} 720w, ${m.src_1080 || m.src} 1080w"
                    sizes="(max-width:768px) 100vw, 50vw"
                    alt="${m.alt}"
                    width="${m.width}"
                    height="${m.height}"
                    class="variant-media-img"
                  />
                </div>`;
      } else if (m.media_type === 'video') {
        return `<div class="media-item swiper-slide" data-index="${i}">
                  <video 
                    controls 
                    muted 
                    preload="metadata" 
                    playsinline 
                    poster="${m.poster || ''}"
                    class="variant-media-video" 
                    style="max-width:100%;"
                  >
                    <source src="${m.src}" type="video/mp4">
                  </video>
                </div>`;
      }
      return '';
    }).join('');

    mediaWrapperHtml.push(`<div class="media-group" data-variant-id="${variant.id}" style="display:none;">
       <div class="swiper media-swiper">
         <div class="swiper-wrapper">${mediaHtml}</div>
         <div class="swiper-pagination"></div>
       </div>
      </div>`);
  });

  leftBox.innerHTML = `<div class="media-wrapper">${mediaWrapperHtml.join('')}</div>`;

   // ✅ 添加心愿单按钮
    const wishlistHtml = `
      <div class="productCard_wishlist">
        <div class="wishlist-engine"
            data-product_id="${product.id}"
            data-variant_id="${product.selected_or_first_available_variant?.id || product.variants[0].id}"
            data-full_button="false"
            data-css="true"></div>
      </div>
    `;
    leftBox.insertAdjacentHTML('beforeend', wishlistHtml);

  // 默认显示第一个变体媒体
  const firstGroup = leftBox.querySelector('.media-group');
  if (firstGroup) firstGroup.style.display = 'block';

  // ====================
  // 右侧价格
  // ====================
  const priceHtml = `<div class="productPopUp_price">
    <span class="currentPrice my_h_18 font-weight-500 t_color4">$${(product.price / 100).toFixed(2)}</span>
    ${product.compare_at_price ? `<span class="underlinedPrice my_h_14 font-weight-400 t_color5">$${(product.compare_at_price / 100).toFixed(2)}</span>` : ''}
  </div>`;

  // ====================
  // 右侧选项
  // ====================
  const optionsHtml = (product.options || []).map(option => {
    let firstActiveSet = false;
    const valuesHtml = (option.values || []).map(value => {
      const inventory = (product.variants || [])
        .filter(v => v.options.includes(value))
        .reduce((sum, v) => sum + v.inventory_quantity, 0);
      const disabled = inventory === 0 ? 'is-disabled' : '';
      let active = '';
      if (!disabled && !firstActiveSet) {
        active = 'active';
        firstActiveSet = true;
      }
      return `<div class="variant-option my_h_14 font-weight-400 ${disabled} ${active}" data-option="${option.name}" data-value="${value}" data-inventory="${inventory}">${value}</div>`;
    }).join('');
    return `<div class="option-group">
            <div class="option-title my_h_16 font-weight-500 t_color2">${option.name}</div>
            <div class="option-values">${valuesHtml}</div>
          </div>`;
  }).join('');

  // ====================
  // Quick Add 按钮 + 数量
  // ====================
  const selectedVariant = product.variants[0];
  const productId = product.id;
  const disabledAttr = selectedVariant.available === false ? 'disabled' : '';
  const quantityHtml = `
    <div class="productPopUp_quantity">
      <div class="quantity-btn minus">-</div>
      <input type="number" id="popup-quantity" 
             name="quantity" 
             class="quantity-input my_h_14 font-weight-400 t_color2" 
             value="1" 
             min="1" 
             ${disabledAttr}>
      <div class="quantity-btn plus">+</div>
    </div>
  `;
  const quickAddHtml = `
    <div class="productPopUp_addCart">
      <product-form data-section-id="${sectionId}">

       <form method="post" action="/cart/add" id="quick-add-${productId}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
       <input type="hidden" name="form_type" value="product">
       <input type="hidden" name="utf8" value="✓">
       <input type="hidden" name="id" value="${productId}" class="product_variantId">

       <button id="quick-add-${productId}-submit" type="submit" name="add" class="productPopUp_addCart_text my_h_16 my_m_14 font-weight-500 t_color3" aria-haspopup="dialog" aria-labelledby="quick-add-${productId}-submit title--${productId}" aria-live="polite" data-sold-out-message="true">
        Add to Cart

        <div class="loading__spinner hidden">
          <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>

        </div>
        </button>
        <input type="hidden" name="product-id" value="${productId}"><input type="hidden" name="section-id" value="${sectionId}"></form>
        </product-form>
        </div>
  `;
  const soldOutHtml = `<div class="productPopUp_soldOut my_h_16 my_m_14 font-weight-500 t_color5" style="${selectedVariant.available ? 'display:none;' : 'display:block;'}">Sold Out</div>`;

  const btnHtml = `<div class="productPopUp_button">
    ${quantityHtml}
    ${quickAddHtml}
    ${soldOutHtml}
  </div>`;
   const judgeHtml = judgeWidget ? judgeWidget.outerHTML : '';

  // ====================
  // 右侧内容整体渲染
  // ====================
  rightBox.innerHTML = `
    <div class="productPopUp_header">
      <div class="productPopUp_title my_h_24 my_m_24 font-weight-500 t_color2 two-row">${product.title}</div>
      ${judgeHtml}  <!-- ✅ 插入 Judge.me 评分 -->
      ${priceHtml}
    </div>
    <div class="productPopUp_description my_h_16 my_m_14 font-weight-400 t_color9 three-row">${product.description}</div>
    ${optionsHtml}
    ${btnHtml}
    <div class="productPopUp_details">
      <a class="my_h_16 my_m_14 font-weight-500 t_color2 text-decoration-line" href="${product.url}">View full details</a>
    </div>
  `;

  // ====================
  // 初始化首个可见 Swiper
  // ====================
  const visibleSwiper = leftBox.querySelector('.media-group[style*="display: block"] .media-swiper');
  if (visibleSwiper) {
    new Swiper(visibleSwiper, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: true,
      pagination: {
        el: visibleSwiper.querySelector('.swiper-pagination'),
        clickable: true
      },
    });
  }
}

// =============================
// 初始化变体选项 + 数量 + Add to Cart
// =============================
function initVariants(product, wrapper) {
  const rightBox = wrapper.querySelector('.productPopUP_right');
  const leftBox = wrapper.querySelector('.productPopUP_left');
  const addCartBtn = rightBox.querySelector('.productPopUp_addCart');
  const soldOutBtn = rightBox.querySelector('.productPopUp_soldOut');
  const quantityInput = rightBox.querySelector('.quantity-input');
  const minusBtn = rightBox.querySelector('.quantity-btn.minus');
  const plusBtn = rightBox.querySelector('.quantity-btn.plus');

  let selectedOptions = {};

  // 默认选中第一个可用变体
  let firstAvailableVariant = product.variants.find(v => v.available) || product.variants[0];

  // 设置初始选项
  product.options.forEach(option => {
    const activeValue = firstAvailableVariant.options.find(optVal => optVal) || '';
    selectedOptions[option.name] = activeValue;
    const el = rightBox.querySelector(`.variant-option[data-option="${option.name}"][data-value="${activeValue}"]`);
    if (el) el.classList.add('active');
  });

  // 初始化数量和 dataset + 隐藏 input
  function updateQuantityAndVariantInput(variant) {
    quantityInput.value = 1;
    quantityInput.max = variant.inventory_quantity;
    addCartBtn.dataset.variant = variant.id;
    addCartBtn.dataset.quantity = 1;

    // 更新隐藏 input 的 value
    const hiddenInput = addCartBtn.querySelector('.product_variantId');
    if (hiddenInput) hiddenInput.value = variant.id;

    soldOutBtn.dataset.variant = variant.id;
    soldOutBtn.dataset.quantity = 0;

    if (variant.available) {
      addCartBtn.style.display = 'flex';
      soldOutBtn.style.display = 'none';
    } else {
      addCartBtn.style.display = 'none';
      soldOutBtn.style.display = 'flex';
    }

    // 显示对应左侧媒体
    leftBox.querySelectorAll('.media-group').forEach(group => {
      group.style.display = group.dataset.variantId == variant.id ? 'block' : 'none';
      const swiperEl = group.querySelector('.media-swiper');
      if (swiperEl) {
        if (swiperEl.swiper) swiperEl.swiper.update();
        else new Swiper(swiperEl, { slidesPerView: 1, spaceBetween: 10, pagination: { el: swiperEl.querySelector('.swiper-pagination'), clickable: true } });
      }
    });
  }

  // 初始化第一次显示
  updateQuantityAndVariantInput(firstAvailableVariant);

  // 绑定选项点击
  rightBox.addEventListener('click', e => {
    const el = e.target.closest('.variant-option');
    if (!el || el.classList.contains('is-disabled')) return;

    const optionName = el.dataset.option;
    const value = el.dataset.value;

    rightBox.querySelectorAll(`.variant-option[data-option="${optionName}"]`).forEach(o => o.classList.remove('active'));
    el.classList.add('active');
    selectedOptions[optionName] = value;

    const matchedVariant = product.variants.find(v =>
      Object.keys(selectedOptions).every(opt => v.options.includes(selectedOptions[opt]))
    );
    if (!matchedVariant) return;

    // 更新数量、dataset、隐藏 input
    updateQuantityAndVariantInput(matchedVariant);
  });

  // 数量按钮
  minusBtn?.addEventListener('click', () => {
    let val = parseInt(quantityInput.value) || 1;
    if (val > 1) quantityInput.value = val - 1;
    addCartBtn.dataset.quantity = quantityInput.value;
  });

  plusBtn?.addEventListener('click', () => {
    let val = parseInt(quantityInput.value) || 1;
    const max = parseInt(quantityInput.max) || Infinity;
    if (val < max) quantityInput.value = val + 1;
    addCartBtn.dataset.quantity = quantityInput.value;
  });

  quantityInput?.addEventListener('input', () => {
    let val = parseInt(quantityInput.value) || 1;
    const max = parseInt(quantityInput.max) || Infinity;
    if (val < 1) val = 1;
    if (val > max) val = max;
    quantityInput.value = val;
    addCartBtn.dataset.quantity = val;
  });

}


document.addEventListener('click', function (e) {
  if (e.target.classList.contains('productDiscount_codeCopy')) {
    // 找到当前折扣码的父容器
    const codeBox = e.target.closest('.productDiscount_code');
    if (!codeBox) return;

    // 获取折扣码内容
    const codeEl = codeBox.querySelector('.productDiscount_codeValue');
    if (!codeEl) return;

    const code = codeEl.textContent.trim();

    // 写入剪贴板
    navigator.clipboard.writeText(code).then(() => {
      // 临时修改按钮文字
      const oldText = e.target.textContent;
      e.target.textContent = 'COPIED!';
      // setTimeout(() => {
      //   e.target.textContent = oldText;
      // }, 8000);
    }).catch(err => {
      console.error('Copy failed:', err);
    });
  }
});













